<template>
  <div class="card mb-25 border-0 radius-7 bg-white sales-pos-location-box hour-analysis-card">
    <div class="card-body">
      <div class="hour-analysis-card__header">
        <div class="title">
          <span class="fw-medium text-muted fs-13 d-block mb-5 text-uppercase">
            Métricas comparativas
          </span>
          <h4 class="card-title fw-black mb-0">Análise de horário</h4>
        </div>
      </div>

      <div class="hour-analysis-card__summary">
        <div class="hour-analysis-card__summary-item">
          <span>Funcionamento</span>
          <strong>{{ operatingWindow }}</strong>
        </div>

        <div class="hour-analysis-card__summary-item">
          <span>Pico de agendamentos</span>
          <strong>{{ peakScheduled.hour }} | {{ peakScheduled.value }}</strong>
        </div>

        <div class="hour-analysis-card__summary-item">
          <span>Pico de cancelamentos</span>
          <strong>{{ peakCanceled.hour }} | {{ peakCanceled.value }}</strong>
        </div>
      </div>

      <div id="weeklySalesChart" class="chart hour-analysis-card__chart-wrap">
        <div class="hour-analysis-card__chart-inner">
          <apexchart
            v-if="chartSeries.length"
            key="hour-analysis-chart"
            type="area"
            :height="chartHeight"
            :options="chartOptions"
            :series="chartSeries"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent } from "vue";
import { useStore } from "vuex";

type HourMetric = {
  scheduled?: number;
  canceled?: number;
};

export default defineComponent({
  name: "MetricsMonthSendAndReceivedHour",
  setup() {
    const store = useStore();

    const getMetricsMonthSendAndReceivedHour = computed(
      () => store.getters.getMetricsMonthSendAndReceivedHour || {}
    );

    const getOperatingHours = computed(
      () =>
        store.getters.getOperatingHours || {
          opening_time: "08:00",
          closing_time: "21:00",
        }
    );

    const weeklySalesChart = {
      chart: {
        zoom: {
          enabled: false,
        },
        toolbar: {
          show: false,
        },
      },
      dataLabels: {
        enabled: false,
      },
      stroke: {
        curve: "smooth",
        width: 2,
      },
      fill: {
        type: "gradient",
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.7,
          opacityTo: 0.12,
          stops: [0, 90, 100],
        },
      },
      colors: ["#06B48A", "#836FFF"],
      legend: {
        position: "top",
        fontSize: "14px",
        fontWeight: 500,
        horizontalAlign: "center",
        fontFamily: "Red Hat Display, sans-serif",
        labels: {
          colors: "#8E8DA1",
        },
        markers: {
          offsetY: -1,
          offsetX: -4,
          height: 15,
          width: 15,
        },
        itemMargin: {
          horizontal: 10,
        },
      },
      grid: {
        show: true,
        strokeDashArray: 5,
        borderColor: "#d9e9ef",
      },
      tooltip: {
        style: {
          fontSize: "14px",
          fontFamily: "Red Hat Display, sans-serif",
        },
      },
      xaxis: {
        axisTicks: {
          show: false,
        },
        axisBorder: {
          show: false,
        },
      },
      yaxis: {
        show: true,
        labels: {
          style: {
            fontFamily: "Red Hat Display, sans-serif",
            colors: ["#9C9AB6"],
            fontSize: "14px",
            fontWeight: 500,
          },
          formatter(value: number) {
            return Math.round(value);
          },
        },
      },
    };

    const chartHeight = computed(() => 280);

    const chartCategories = computed(() => {
      const operatingHours = getOperatingHours.value;
      const [openHour] = operatingHours.opening_time.split(":").map(Number);
      let [closeHour, closeMinute] = operatingHours.closing_time.split(":").map(Number);

      if (closeMinute > 0) {
        closeHour += 1;
      }

      const hours: string[] = [];
      for (let i = openHour; i <= closeHour; i += 1) {
        hours.push(`${String(i).padStart(2, "0")}:00`);
      }
      return hours;
    });

    const operatingWindow = computed(() => {
      const operatingHours = getOperatingHours.value;
      return `${operatingHours.opening_time} - ${operatingHours.closing_time}`;
    });

    const chartOptions = computed(() => ({
      ...weeklySalesChart,
      stroke: {
        ...weeklySalesChart.stroke,
        width: 2,
      },
      legend: {
        ...weeklySalesChart.legend,
        position: "bottom",
        fontSize: "12px",
      },
      xaxis: {
        ...weeklySalesChart.xaxis,
        categories: chartCategories.value,
        labels: {
          show: true,
          rotate: 0,
          hideOverlappingLabels: true,
          trim: false,
          style: {
            fontFamily: "Red Hat Display, sans-serif",
            colors: "#9C9AB6",
            fontSize: "11px",
            fontWeight: 500,
          },
        },
      },
    }));

    const chartSeries = computed(() => {
      const metrics = getMetricsMonthSendAndReceivedHour.value;
      if (!metrics || Object.keys(metrics).length === 0) {
        return [];
      }

      const scheduled = chartCategories.value.map(
        (hour: string) => metrics[hour]?.scheduled ?? 0
      );
      const canceled = chartCategories.value.map(
        (hour: string) => metrics[hour]?.canceled ?? 0
      );

      return [
        {
          name: "Agendamentos",
          data: scheduled,
        },
        {
          name: "Agendamentos cancelados",
          data: canceled,
        },
      ];
    });

    const getPeakMetric = (metricKey: "scheduled" | "canceled") =>
      chartCategories.value.reduce(
        (best: { hour: string; value: number }, hour: string) => {
          const metric = getMetricsMonthSendAndReceivedHour.value[hour] as HourMetric | undefined;
          const currentValue = metric?.[metricKey] ?? 0;

          if (currentValue > best.value) {
            return {
              hour,
              value: currentValue,
            };
          }

          return best;
        },
        {
          hour: chartCategories.value[0] || "--:--",
          value: 0,
        }
      );

    const peakScheduled = computed(() => getPeakMetric("scheduled"));
    const peakCanceled = computed(() => getPeakMetric("canceled"));

    return {
      chartHeight,
      chartOptions,
      chartSeries,
      operatingWindow,
      peakCanceled,
      peakScheduled,
    };
  },
});
</script>

<style scoped>
.hour-analysis-card .card-body {
  padding: 18px;
}

.hour-analysis-card__header {
  margin-bottom: 14px;
}

.hour-analysis-card__summary {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 12px;
}

.hour-analysis-card__summary-item {
  border: 1px solid #eef0f7;
  border-radius: 7px;
  padding: 12px;
}

.hour-analysis-card__summary-item span {
  display: block;
  margin-bottom: 6px;
  color: #8e8da1;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.hour-analysis-card__summary-item strong {
  display: block;
  color: #2b2a3f;
  font-size: 16px;
  line-height: 1.2;
}

.hour-analysis-card__chart-wrap {
  width: 100%;
}

.hour-analysis-card__chart-inner {
  width: 100%;
}

@media (max-width: 991px) {
  .hour-analysis-card__summary {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 767px) {
  .hour-analysis-card .card-body {
    padding: 16px 14px;
  }

  .hour-analysis-card__summary {
    gap: 8px;
  }

  .hour-analysis-card__summary-item {
    padding: 10px;
    border-radius: 7px;
  }

  .hour-analysis-card__summary-item strong {
    font-size: 15px;
  }

  .hour-analysis-card__chart-wrap {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    padding-bottom: 6px;
  }

  .hour-analysis-card__chart-inner {
    min-width: 640px;
  }
}
</style>

<style>
body.dark .hour-analysis-card__summary-item {
  background: #2b2a3f;
  border-color: #45445f;
}

body.dark .hour-analysis-card__summary-item span {
  color: #b8b6cf;
}

body.dark .hour-analysis-card__summary-item strong {
  color: #ffffff;
}
</style>
